/**
 * Recursive-descent parser for the equation language (plan §5.1).
 *
 * Grammar, loosest binding first:
 *
 *   compare  := add ( ('<' | '>' | '<=' | '>=' | '==' | '!=') add )*
 *   add      := multiply ( ('+' | '-') multiply )*
 *   multiply := unary ( ('*' | '/') unary )*
 *   unary    := ('+' | '-') unary | power
 *   power    := atom ( '^' unary )?            // right-associative, binds tighter than unary minus
 *   atom     := number | identifier | call | '(' compare ')'
 *   call     := identifier '(' [ compare ( ',' compare )* ] ')'
 *
 * Comparisons yield 1 or 0 so they can be fed straight into `if(...)`, which is how every conditional
 * fee in the seed policy is written.
 */
import { throwEngineError } from '../errors.js';
import { normalizeExpression, tokenize } from './tokenizer.js';
export const COMPARISON_OPERATORS = ['<', '>', '<=', '>=', '==', '!='];
/** The whole function library, with its arity range. Nothing else may be called (plan §5.1). */
export const FUNCTIONS = {
    min: { min: 1, max: 32 },
    max: { min: 1, max: 32 },
    round: { min: 1, max: 1 },
    ceil: { min: 1, max: 1 },
    floor: { min: 1, max: 1 },
    abs: { min: 1, max: 1 },
    if: { min: 3, max: 3 },
};
export const FUNCTION_NAMES = Object.keys(FUNCTIONS);
export const isFunctionName = (value) => Object.prototype.hasOwnProperty.call(FUNCTIONS, value);
export const parseExpression = (source) => {
    const tokens = tokenize(source);
    const references = [];
    const seen = new Set();
    let index = 0;
    const peek = () => tokens[index];
    const describe = (token) => (token ? `"${token.value}"` : 'the end of the expression');
    const expect = (value) => {
        const token = peek();
        if (!token || token.value !== value) {
            return throwEngineError('SYNTAX', `Expected "${value}" but found ${describe(token)}`, {
                position: token?.start,
                token: token?.value,
            });
        }
        index += 1;
        return token;
    };
    const atOperator = (...operators) => {
        const token = peek();
        return token && token.type === 'operator' && operators.includes(token.value) ? token.value : null;
    };
    const atom = () => {
        const token = peek();
        if (!token) {
            return throwEngineError('SYNTAX', 'The expression ends where a number, an input or a bracket was expected');
        }
        if (token.type === 'lparen') {
            index += 1;
            const inner = compare();
            const close = expect(')');
            return { ...inner, start: token.start, end: close.end };
        }
        if (token.type === 'number') {
            index += 1;
            if (!Number.isFinite(Number(token.value))) {
                return throwEngineError('SYNTAX', `"${token.value}" is not a valid number`, { position: token.start, token: token.value });
            }
            return { type: 'number', value: token.value, start: token.start, end: token.end };
        }
        if (token.type === 'identifier') {
            index += 1;
            if (peek()?.type === 'lparen') {
                if (!isFunctionName(token.value)) {
                    return throwEngineError('UNKNOWN_FUNCTION', `"${token.value}" is not one of ${FUNCTION_NAMES.join(', ')}`, {
                        position: token.start,
                        token: token.value,
                    });
                }
                const name = token.value;
                index += 1;
                const args = [];
                if (peek()?.type !== 'rparen') {
                    args.push(compare());
                    while (peek()?.type === 'comma') {
                        index += 1;
                        args.push(compare());
                    }
                }
                const close = expect(')');
                const arity = FUNCTIONS[name];
                if (args.length < arity.min || args.length > arity.max) {
                    const expected = arity.min === arity.max ? `${arity.min}` : `${arity.min} to ${arity.max}`;
                    return throwEngineError('ARITY', `${name}() takes ${expected} argument(s) but was given ${args.length}`, {
                        position: token.start,
                        token: name,
                    });
                }
                return { type: 'call', name, args, start: token.start, end: close.end };
            }
            if (!seen.has(token.value)) {
                seen.add(token.value);
                references.push(token.value);
            }
            return { type: 'variable', name: token.value, start: token.start, end: token.end };
        }
        return throwEngineError('SYNTAX', `Expected a number, an input or a bracket but found ${describe(token)}`, {
            position: token.start,
            token: token.value,
        });
    };
    const power = () => {
        const base = atom();
        if (atOperator('^')) {
            index += 1;
            const exponent = unary();
            return { type: 'binary', op: '^', left: base, right: exponent, start: base.start, end: exponent.end };
        }
        return base;
    };
    function unary() {
        const token = peek();
        if (token && token.type === 'operator' && (token.value === '+' || token.value === '-')) {
            index += 1;
            const operand = unary();
            return { type: 'unary', op: token.value, operand, start: token.start, end: operand.end };
        }
        return power();
    }
    const multiply = () => {
        let left = unary();
        let op = atOperator('*', '/');
        while (op) {
            index += 1;
            const right = unary();
            left = { type: 'binary', op, left, right, start: left.start, end: right.end };
            op = atOperator('*', '/');
        }
        return left;
    };
    const add = () => {
        let left = multiply();
        let op = atOperator('+', '-');
        while (op) {
            index += 1;
            const right = multiply();
            left = { type: 'binary', op, left, right, start: left.start, end: right.end };
            op = atOperator('+', '-');
        }
        return left;
    };
    function compare() {
        let left = add();
        let op = atOperator(...COMPARISON_OPERATORS);
        while (op) {
            index += 1;
            const right = add();
            left = { type: 'binary', op, left, right, start: left.start, end: right.end };
            op = atOperator(...COMPARISON_OPERATORS);
        }
        return left;
    }
    const ast = compare();
    const trailing = peek();
    if (trailing) {
        return throwEngineError('SYNTAX', `Unexpected ${describe(trailing)} after a complete expression`, {
            position: trailing.start,
            token: trailing.value,
        });
    }
    return { source: normalizeExpression(source), tokens, ast, references };
};
//# sourceMappingURL=parser.js.map